<template>
  <View direction="column" gap="md">
    <FileListItem
      class="clickable"
      v-for="(file, index) in files"
      :variant="variant"
      :file-name="file.fileName"
      :file-size="file.fileSize"
      :thumbnail-url="file.thumbnailUrl"
      :key="file.id"
      :is-removable="canRemoveFile(file)"
      @click="fileViewerModalRef?.show(index)"
      @remove="$emit('remove', file)"
    />

    <FileListItem
      v-for="uploadFile in uploadFiles"
      :variant="variant"
      :file-name="uploadFile.file.name"
      :file-size="uploadFile.file.size"
      :uploaded="uploadFile.uploaded"
      :key="uploadFile.id"
    />

    <FileViewerModal :files="files" ref="fileViewerModalRef">
      <template #actions="{ activeFile }">
        <IconButton
          v-if="canRemoveFile(activeFile)"
          variant="secondary"
          size="medium"
          shape="square"
          icon="trash"
          @click="$emit('remove', activeFile)"
        />
      </template>
    </FileViewerModal>
  </View>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

import { IconButton, View } from '@tallo/design-system';

import { FileListItemVariant } from '../interfaces/file-list-item-variant';
import { FileDto } from '../interfaces/file.dto';
import { UploadFile } from '../interfaces/upload-file.interface';
import FileListItem from './file-list-item.vue';
import FileViewerModal from './file-viewer-modal.vue';

interface FileListProps {
  files?: FileDto[];
  uploadFiles?: UploadFile[];
  isRemovable?: boolean;
  isFileRemovable?: (file: FileDto) => boolean;
  variant?: FileListItemVariant;
}

const props = withDefaults(defineProps<FileListProps>(), {
  variant: 'outline',
});
defineEmits<{ remove: [file: FileDto] }>();

const fileViewerModalRef = ref<InstanceType<typeof FileViewerModal>>();
const files = computed(() => props.files || []);

function canRemoveFile(file?: FileDto): boolean {
  if (!props.isRemovable) {
    return false;
  }

  return !file || !props.isFileRemovable || props.isFileRemovable(file);
}
</script>

<style scoped lang="scss">
.clickable:hover {
  cursor: pointer;
}
</style>
